/**
 * RadarChart Component
 *
 * A radar chart with 4 equal quadrant groups.
 * Features:
 * - 4 groups with 90° each
 * - Sectors distributed proportionally within groups
 * - Crosshair axis dividers with labels
 * - Multiple score layers with indicators
 * - Compound/aggregate scores for idle state
 * - Hover effects for multi-layer mode
 * - Top 3 / Bottom 3 rim coloring
 */

import React, { useMemo, useState } from "react";
import { orderBy, sumBy } from "lodash";
import { cn } from "@/shared/utils/shadcn";
import { AvatarWithInitials } from "@/shared/ui/Avatar";
import { RadarChartProps, ScorePoint } from "./types";
import {
  computeGroups,
  calculateScorePoints,
  generateBlobPath,
  generateTextArcPath,
  generateRimArcPath,
  generateSectorPath,
  generateAxisLine,
  shouldFlipText,
  getRimColorClass,
  polarToCartesian,
  CENTER_X,
  CENTER_Y,
  CHART_RADIUS,
  SECTOR_LABEL_RADIUS,
  GROUP_LABEL_RADIUS,
  AXIS_LINE_INNER_RADIUS,
  AXIS_LINE_OUTER_RADIUS,
  AXIS_LABEL_RADIUS,
} from "./utils";
import styles from "./index.module.scss";

/**
 * Calculate indicator position based on data points
 * Matches DimensionsChart getMemberPosition logic
 */
const getIndicatorPosition = (points: ScorePoint[]) => {
  const xSum = sumBy(points, (p) => p.x);
  const ySum = sumBy(points, (p) => p.y);

  const avgX = xSum / points.length;
  const avgY = ySum / points.length;

  // Transform to spread indicators like DimensionsChart
  const userCenterX = avgX - CENTER_X / 2;
  const userCenterY = avgY;

  return {
    x: userCenterX * 3 - 30,
    y: userCenterY - 2,
  };
};

export const RadarChart: React.FC<RadarChartProps> = ({
  groups,
  axisLabels,
  data,
  defaultScores,
  defaultActiveId,
  onSectorClick,
  onIndicatorClick,
  className,
}) => {
  const [hoveredDataId, setHoveredDataId] = useState<string | number | null>(
    null
  );

  // Active ID is either hovered or default
  const activeDataId = hoveredDataId ?? defaultActiveId ?? null;

  // Compute groups with angles
  const computedGroups = useMemo(() => computeGroups(groups), [groups]);

  // Flatten all computed sectors
  const allSectors = useMemo(
    () => computedGroups.flatMap((g) => g.computedSectors),
    [computedGroups]
  );

  // Calculate default/compound score points (aggregate like team average)
  const defaultScorePoints = useMemo(() => {
    if (!defaultScores) return null;
    return calculateScorePoints(defaultScores, computedGroups);
  }, [defaultScores, computedGroups]);

  // Calculate score points for each data layer
  const dataLayers = useMemo(() => {
    return data.map((d) => ({
      ...d,
      points: calculateScorePoints(d.scores, computedGroups),
    }));
  }, [data, computedGroups]);

  // Multi-layer mode (show indicators when more than 1 layer)
  const isMultiLayer = data.length > 1;

  // Get active layer
  const activeLayer = activeDataId
    ? dataLayers.find((d) => d.id === activeDataId)
    : null;

  // Axis angles (0=right, 90=bottom, 180=left, 270=top)
  const axisAngles = [0, 90, 180, 270];

  /**
   * Render blobs based on state
   *
   * Logic matches DimensionsChart for smooth transitions:
   * - Always render both background and foreground blobs
   * - Background transitions between primary ↔ gray
   * - Foreground shows active member's data with primary color
   *
   * States:
   * - Single layer without team data: Show single primary blob
   * - Single layer with team data: Background (gray team avg) + foreground (primary individual)
   * - Multi-layer idle: Background blob (primary), foreground hidden
   * - Multi-layer active: Background (gray) + foreground (primary)
   */
  const renderBlobs = () => {
    // Single layer mode
    if (!isMultiLayer) {
      const layer = dataLayers[0];
      if (!layer) return null;

      // If we have defaultScores (team average), show it as background
      if (defaultScorePoints) {
        return (
          <>
            {/* Background blob - team average (gray) */}
            <path
              key="blob-background"
              className="fill-gray-300 transition-all duration-300 ease-in-out"
              d={generateBlobPath(defaultScorePoints)}
            />
            {/* Foreground blob - individual scores (primary) */}
            <path
              key="blob-foreground"
              className="fill-primary transition-all duration-300 ease-in-out"
              d={generateBlobPath(layer.points)}
            />
          </>
        );
      }

      // No team data - just show single blob
      return (
        <path
          key={`blob-${layer.id}`}
          className="fill-primary transition-all duration-300 ease-in-out"
          d={generateBlobPath(layer.points)}
        />
      );
    }

    // Multi-layer mode - always render both blobs for smooth transitions
    // Background points (always default/compound)
    const backgroundPoints = defaultScorePoints || dataLayers[0]?.points;

    // Foreground points (active member or same as background when idle)
    const foregroundPoints = activeLayer?.points || backgroundPoints;

    return (
      <>
        {/* Background blob - transitions between primary (idle) and gray (active) */}
        {backgroundPoints && (
          <path
            key="blob-background"
            className={cn(
              "transition-all duration-300 ease-in-out",
              activeLayer ? "fill-gray-300" : "fill-primary"
            )}
            d={generateBlobPath(backgroundPoints)}
          />
        )}
        {/* Foreground blob - always rendered, shows active member's data */}
        {foregroundPoints && (
          <path
            key="blob-foreground"
            className={cn(
              "fill-primary transition-all duration-300 ease-in-out",
              // Only visible when there's an active layer
              activeLayer ? "opacity-100" : "opacity-0"
            )}
            d={generateBlobPath(foregroundPoints)}
          />
        )}
      </>
    );
  };

  // Get scores for display (from active layer or first layer)
  const displayScores = useMemo(() => {
    const layer = activeLayer || dataLayers[0];
    return (!defaultActiveId ? defaultScores : layer?.scores) || {};
  }, [activeLayer, dataLayers, defaultScores]);

  // Render center avatar for single-layer mode
  // Positioned at the centroid of the blob (same as multi-layer mode)
  const renderCenterAvatar = () => {
    if (isMultiLayer) return null;

    const layer = dataLayers[0];
    if (!layer?.indicator?.avatar && !layer?.indicator?.initials) return null;

    // Use same positioning logic as multi-layer indicators
    const position = getIndicatorPosition(layer.points);
    const avatarSize = 24;

    return (
      <g
        className="cursor-pointer"
        transform={`translate(${position.x}, ${position.y}) scale(0.20, 0.20)`}
      >
        <foreignObject
          x={0}
          y={0}
          width={avatarSize}
          height={avatarSize}
          className="overflow-visible"
        >
          <div className="w-full h-full fixed rounded-full overflow-hidden z-30">
            <AvatarWithInitials
              avatar={layer.indicator.avatar || ""}
              initials={layer.indicator.initials || ""}
              className="w-full h-full object-cover border border-white border-solid"
            />
          </div>
        </foreignObject>
      </g>
    );
  };

  // Render indicators (avatars)
  const renderIndicators = () => {
    if (!isMultiLayer) return null;

    // If defaultActiveId is set, only show that indicator
    // Otherwise show all indicators
    const layersToShow = defaultActiveId
      ? dataLayers.filter((d) => d.id === defaultActiveId)
      : dataLayers;

    // Sort so active avatar renders last (on top)
    const sortedLayers = orderBy(
      layersToShow,
      (d) => d.id === activeDataId,
      "asc"
    );

    return sortedLayers.map((layer) => {
      const position = getIndicatorPosition(layer.points);
      const isActive = layer.id === activeDataId;
      const avatarSize = 24;

      return (
        <g
          key={`indicator-${layer.id}`}
          className="cursor-pointer"
          transform={`translate(${position.x}, ${position.y}) scale(0.20, 0.20)`}
        >
          <foreignObject
            stroke="white"
            x={0}
            y={0}
            width={avatarSize}
            height={avatarSize}
            className="overflow-visible"
            onMouseEnter={() => setHoveredDataId(layer.id)}
            onMouseLeave={() => setHoveredDataId(null)}
            onClick={() => onIndicatorClick?.(layer)}
          >
            <div
              className={cn(
                "w-full h-full fixed rounded-full overflow-hidden transition-transform duration-200 ease-in-out z-30 hover:z-50 hover:scale-125",
                isActive && "scale-125 z-50"
              )}
            >
              {layer.indicator?.avatar || layer.indicator?.initials ? (
                <AvatarWithInitials
                  avatar={layer.indicator.avatar || ""}
                  initials={layer.indicator.initials || ""}
                  className="w-full h-full object-cover border border-white border-solid"
                />
              ) : (
                <div
                  className="w-full h-full flex items-center justify-center text-white text-xs font-medium border border-white border-solid"
                  style={{
                    backgroundColor: layer.indicator?.color || "#6366f1",
                  }}
                >
                  {String(layer.id).charAt(0).toUpperCase()}
                </div>
              )}
            </div>
          </foreignObject>
        </g>
      );
    });
  };

  return (
    <div
      className={cn(
        "flex flex-1 w-full h-full max-h-[755px] relative",
        className
      )}
    >
      <svg
        viewBox="0 0 100 100"
        xmlns="http://www.w3.org/2000/svg"
        className={cn(styles.main)}
      >
        {/* Score blobs */}
        {renderBlobs()}

        {/* Sectors (interactive areas with labels and rims) */}
        {allSectors.map((sector) => {
          const group = computedGroups[sector.groupIndex];
          const flip = shouldFlipText(sector.midAngle);
          const pathId = `sector-label-${sector.id}`;
          // Calculate score position at the rim
          const scorePosition = polarToCartesian(sector.midAngle, CHART_RADIUS);
          const sectorScore = displayScores[sector.id] ?? sector.score ?? 0;

          return (
            <g
              key={sector.id}
              className="group cursor-pointer"
              onClick={() => onSectorClick?.(sector, group)}
            >
              {/* Clickable area */}
              <path
                className="fill-transparent stroke-gray-500/10 stroke-[0.05] transition-all duration-300 group-hover:fill-white/30 group-hover:stroke-gray-500/30"
                d={generateSectorPath(
                  sector.startAngle,
                  sector.endAngle,
                  CHART_RADIUS
                )}
              />

              {/* Rim arc (colored based on score rank) */}
              <path
                className={cn(
                  "fill-none stroke-[1.5] transition-all duration-300 group-hover:stroke-[2]",
                  getRimColorClass(sector.colorRank)
                )}
                d={generateRimArcPath(
                  sector.startAngle,
                  sector.endAngle,
                  CHART_RADIUS
                )}
              />

              {/* Score circle (shown on hover) */}
              <g
                transform={`translate(${scorePosition.x}, ${scorePosition.y})`}
              >
                <circle
                  r="1.5"
                  className="fill-primary opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"
                />
                <text
                  className="text-[1.5px] font-medium fill-white opacity-0 transition-opacity duration-300 ease-in-out group-hover:opacity-100"
                  textAnchor="middle"
                  y=".5"
                >
                  {Math.round(sectorScore)}
                </text>
              </g>

              {/* Label path */}
              <path
                id={pathId}
                className="fill-none stroke-none"
                d={generateTextArcPath(
                  sector.startAngle,
                  sector.endAngle,
                  SECTOR_LABEL_RADIUS,
                  flip
                )}
              />

              {/* Sector label */}
              <text className="text-[1.5px] fill-gray-600/60 transition-colors duration-300 group-hover:fill-primary">
                <textPath
                  xlinkHref={`#${pathId}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {sector.label}
                </textPath>
              </text>
            </g>
          );
        })}

        {/* Axis lines (crosshairs) */}
        {axisAngles.map((angle, index) => (
          <path
            key={`axis-${index}`}
            className="stroke-gray-400/30 stroke-[0.15]"
            d={generateAxisLine(
              angle,
              AXIS_LINE_INNER_RADIUS,
              AXIS_LINE_OUTER_RADIUS
            )}
          />
        ))}

        {/* Group labels */}
        {computedGroups.map((group) => {
          const flip = shouldFlipText(group.midAngle);
          const pathId = `group-label-${group.id}`;
          const padding = 4;

          return (
            <g key={`group-${group.id}`}>
              <path
                id={pathId}
                className="fill-none stroke-none"
                d={generateTextArcPath(
                  group.startAngle + padding,
                  group.endAngle - padding,
                  GROUP_LABEL_RADIUS,
                  flip
                )}
              />
              <text className="text-[2.5px] font-medium fill-gray-700/80">
                <textPath
                  xlinkHref={`#${pathId}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {group.label}
                </textPath>
              </text>
            </g>
          );
        })}

        {/* Axis labels (Control, Self, Freedom, Other) */}
        {axisLabels.map((label, index) => {
          const angle = axisAngles[label.position];
          const flip = shouldFlipText(angle);
          const pathId = `axis-label-${index}`;
          const arcSpan = 25;

          return (
            <g key={`axis-label-${index}`}>
              <path
                id={pathId}
                className="fill-none stroke-none"
                d={generateTextArcPath(
                  angle - arcSpan,
                  angle + arcSpan,
                  AXIS_LABEL_RADIUS,
                  flip
                )}
              />
              <text className="text-[2px] fill-gray-500/70">
                <textPath
                  xlinkHref={`#${pathId}`}
                  startOffset="50%"
                  textAnchor="middle"
                >
                  {label.label}
                </textPath>
              </text>
            </g>
          );
        })}

        {/* Center avatar (single-layer mode) */}
        {renderCenterAvatar()}

        {/* Data indicators (avatars for multi-layer mode) */}
        {renderIndicators()}
      </svg>
    </div>
  );
};

// Export types and utilities
export * from "./types";
export { computeGroups, calculateScorePoints, generateBlobPath } from "./utils";

export default RadarChart;
